// ============================================================================
// ROLE-BASED ACTION FILTERING HOOK
// ============================================================================
//
// PURPOSE: Filter actions based on user roles using whitelist approach
//
// FEATURES:
// - Whitelist-based role filtering (allowedRoles only)
// - Works with both DetailAction and ActionConfig interfaces
// - Memoized for performance
// - Type-safe role checking
//
// USAGE:
// - Import and use in action rendering components
// - Provides filtered actions based on current user role
// - Automatically handles role-based access control
//
// ============================================================================

import { useMemo } from "react";
import { useRoleCheck } from "../auth/useRoleCheck";
import { DetailAction } from "@/components/shared/UniversalDetailPage/types/detailPage";
import { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import { useSequencesAccess } from "@/lib/features/use-sequences-access";
import { ENROLL_IN_SEQUENCE_ACTION_ID } from "@/lib/actions/definitions/shared/sequence-actions";

export function useActionFiltering() {
  const { roleName, isAdmin, isEmployee, isClientUser } = useRoleCheck();
  // Frontend-only rollout gate: the "Add to sequence" action is visible ONLY to
  // the allowlisted emails. See lib/features/sequences-access.ts (not a security
  // boundary — just keeps the in-progress feature out of other people's UI).
  const canUseSequences = useSequencesAccess();

  // Drop email-gated actions the current user isn't allowed to see.
  const passesEmailGate = (actionId: unknown): boolean => {
    if (actionId === ENROLL_IN_SEQUENCE_ACTION_ID) return canUseSequences;
    return true;
  };

  const filterActionsByRole = useMemo(() => {
    return <T extends BaseEntity>(actions: DetailAction<T>[]): DetailAction<T>[] => {
      const filtered = actions.filter((action) => {
        if (!passesEmailGate(action.id)) return false;

        // If no allowedRoles specified, action is visible to all roles
        if (!action.allowedRoles || action.allowedRoles.length === 0) {
          return true;
        }

        // Check if current role is in the allowed roles list
        const allowed = roleName ? action.allowedRoles.includes(roleName) : false;
        return allowed;
      });
      return filtered;
    };
  }, [roleName, canUseSequences]);

  const filterActionConfigsByRole = useMemo(() => {
    return (actions: any[]): any[] => {
      return actions.filter((action) => {
        if (!passesEmailGate(action?.id)) return false;

        // If no allowedRoles specified, action is visible to all roles
        if (!action.allowedRoles || action.allowedRoles.length === 0) {
          return true;
        }

        // Check if current role is in the allowed roles list
        return roleName ? action.allowedRoles.includes(roleName) : false;
      });
    };
  }, [roleName, canUseSequences]);

  return {
    filterActionsByRole,
    filterActionConfigsByRole,
    roleName,
    isAdmin,
    isEmployee,
    isClientUser,
  };
}
